import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useForm } from "@tanstack/react-form";

import { addCategorySchema } from "@/zod-schemas/services/add-category-schema";

import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "../common/required-input";
import { Separator } from "@/components/ui/separator";

import { toast } from "sonner";
import { useState } from "react";

import { useUpdateCategory } from "@/hooks/services/use-categories";
import type { Category } from "@/types/services/api-types/category-types";
import { getCategoryFormValues } from "@/default-form-values/services/default-category-form-values";

interface EditCategoryDialogProps {
  category: Category;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditCategoryDialog({
  category,
  open,
  onOpenChange,
}: EditCategoryDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const updateCategory = useUpdateCategory();

  const form = useForm({
    defaultValues: getCategoryFormValues(category),
    validators: {
      onSubmit: addCategorySchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const validated = addCategorySchema.parse(value);
        await updateCategory.mutateAsync({
          id: category.id,
          data: {
            name: validated.name,
          },
        });
        toast.success(`Category "${validated.name}" updated successfully`);
        onOpenChange(false);
      } catch (error: any) {
        const statusCode = error?.response?.status;
        
        if (statusCode === 409) {
          setServerError(`A category with the name "${value.name}" already exists`);
        } else {
          toast.error(`Failed to update category "${value.name}"`);
          onOpenChange(false);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          form.reset();
          setServerError(null);
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Category</DialogTitle>
        </DialogHeader>
        <Separator />

        <form
          className="mb-4"
          id="edit-category-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Name
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setServerError(null);
                      }}
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {serverError && <FieldError>{serverError}</FieldError>}
                  </Field>
                );
              }}
            />
          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" form="edit-category-form" className="w-24">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
